import type { ComputedRef, Ref } from 'vue';
import {
  computed,
  nextTick,
  ref,
} from 'vue';
import { useRoute } from 'vue-router';

import type { IChangeHistoryEntry } from '@modules/change-history/components/VChangeHistory/types';
import type { IColumnForSave, ITableColumn } from '@modules/general-settings/submodules/table-columns-settings/submodules/edit/types';
import sortByDate from '@modules/core/utils/sortByDate';
import { ADMIN_GENERAL_SETTINGS_TABLE_COLUMNS_SETTINGS_PERMISSIONS } from '@modules/general-settings/submodules/table-columns-settings/constants';
import { useSaveTableColumnsMutation } from '@modules/general-settings/submodules/table-columns-settings/submodules/edit/mutations';
import {
  useHistoryChangesOfTableColumnsQuery,
  useTableColumnsQuery,
} from '@modules/general-settings/submodules/table-columns-settings/submodules/edit/queries';
import {
  TableColumnsListType,
} from '@modules/general-settings/submodules/table-columns-settings/submodules/edit/types';
import { sortByPosition } from '@modules/general-settings/submodules/table-columns-settings/submodules/edit/utils';
import { usePermissionsStore } from '@modules/permissions/store';
import { isString } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

export interface ITableColumnsSettingsComposable {
  columns: Ref<ITableColumn[][] | undefined>;
  isSelectedPermanent: Ref<boolean>;
  isOnlyPermanentLeft: Ref<boolean>;
  isEditEnabled: Ref<boolean>;
  isHistoryEnabled: Ref<boolean>;
  historyList: ComputedRef<IChangeHistoryEntry[]>;
  currentTableKey: ComputedRef<string>;
  isEnabledLoading: Ref<boolean | undefined>;
  isDisabledLoading: Ref<boolean | undefined>;
  isSaveLoading: Ref<boolean | undefined>;
  saveColumns(): void;
  onSelection(value: ITableColumn[][]): void;
  onMoveAllToTarget(): Promise<void>;
}

export default function useTableColumnsSettings(): ITableColumnsSettingsComposable {
  const columns = ref<ITableColumn[][]>();
  const isSelectedPermanent = ref(false);

  const route = useRoute();
  const { hasPermission } = usePermissionsStore();

  const tableId = computed<number>(() => (isString(route.params.id) ? Number(route.params.id) : 0));
  const currentTableKey = computed<string>(() => (isString(route.params.localeKey) ? route.params.localeKey : ''));

  const isEditEnabled = computed<boolean>(() => hasPermission(ADMIN_GENERAL_SETTINGS_TABLE_COLUMNS_SETTINGS_PERMISSIONS.EDIT).value);
  const isHistoryEnabled = computed<boolean>(() => hasPermission(ADMIN_GENERAL_SETTINGS_TABLE_COLUMNS_SETTINGS_PERMISSIONS.SHOW_VERSIONS).value);

  const { data: enabledList, isLoading: isEnabledLoading } = useTableColumnsQuery(TableColumnsListType.ENABLED, tableId, isEditEnabled);
  const { data: disabledList, isLoading: isDisabledLoading } = useTableColumnsQuery(TableColumnsListType.DISABLED, tableId, isEditEnabled);
  const { data: historyData } = useHistoryChangesOfTableColumnsQuery(tableId, isHistoryEnabled);

  const { mutateAsync, isLoading: isSaveLoading } = useSaveTableColumnsMutation();

  const historyList = computed<IChangeHistoryEntry[]>(() => (historyData.value?.list ? sortByDate(historyData.value.list) : []));

  const isOnlyPermanentLeft = computed<boolean>(() => !!columns.value?.[0]?.every((col) => col.permanent));

  watchImmediate([enabledList, disabledList], ([enabledList, disabledList]) => {
    if (enabledList && disabledList) {
      columns.value = [
        sortByPosition(enabledList.list).map((item) => ({ ...item, id: String(item.id) })),
        disabledList.list.map((item) => ({ ...item, id: String(item.id) })),
      ];
    }
  });

  function prepareColumnsToSave([enabled = [], disabled = []]: ITableColumn[][] = []): IColumnForSave[] {
    return [
      ...enabled.map((column, index) => ({
        column_id: Number(column.id),
        enabled: true,
        position: index + 1,
      })),
      ...disabled.map((column) => ({
        column_id: Number(column.id),
        enabled: false,
        position: null,
      })),
    ];
  }

  function saveColumns(): void {
    const data = prepareColumnsToSave(columns.value);

    if (data.length) {
      void mutateAsync({
        table_id: tableId.value,
        table_column_for_displays: data,
      });
    }
  }

  function onSelection(value: ITableColumn[][]): void {
    isSelectedPermanent.value = value?.[0]?.some((column) => column.permanent);
  }

  async function onMoveAllToTarget(): Promise<void> {
    await nextTick();

    if (!columns.value?.length) {
      return;
    }

    columns.value = [
      [...columns.value[1].filter((col) => col.permanent)],
      [...columns.value[1].filter((col) => !col.permanent)],
    ];
  }

  return {
    columns,
    isSelectedPermanent,
    isOnlyPermanentLeft,
    isEditEnabled,
    isHistoryEnabled,
    historyList,
    currentTableKey,
    isEnabledLoading,
    isDisabledLoading,
    isSaveLoading,
    saveColumns,
    onSelection,
    onMoveAllToTarget,
  };
}
